import { useState } from "react";
import { Link } from "react-router";
import { HIREOS_INQUIRY_URL } from "@shared/hireos-links";
import {
  collectFieldErrors,
  emailRule,
  isValidPhone,
  type FieldErrors,
} from "@shared/validators";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { inputStyles } from "@shared/form-styles";
import { useSeo } from "@/lib/use-seo";
import { breadcrumbJsonLd } from "@/lib/structured-data";

const PAGE_TITLE = "Contact Us | Accent Productions";

const PAGE_DESCRIPTION =
  "Get in touch with Accent Productions for sound equipment hire in Wellington. Call +64 4 887 8650 or email hello@accent-productions.co.nz. Free quotes for all event types.";

/**
 * Ported from web/src/app/contact/page.tsx (Next.js App Router).
 *
 * - next/metadata -> useSeo hook (#147): title, description, canonical,
 *   and the BreadcrumbJsonLd from the old contact/layout.tsx.
 * - next/link -> react-router <Link>.
 * - Valid submissions open a pre-addressed email to Accent Productions.
 *
 * Email / phone validation comes from @shared/validators — the same
 * primitives the inquiry wizard uses — surfaced through a per-field error
 * map so each field shows its own empty-vs-malformed message.
 */
type ContactFormField = "name" | "email" | "phone" | "message";

/**
 * Exported for tests (#334 item 5) — the component is the only production
 * caller. Same collectFieldErrors pattern as the inquiry step validators.
 */
export function validateContactForm(data: {
  name: string;
  email: string;
  phone: string;
  message: string;
}): FieldErrors<ContactFormField> {
  return collectFieldErrors<ContactFormField>({
    name: { value: data.name, rule: { required: true, emptyMsg: "Name is required" } },
    email: { value: data.email, rule: { required: true, emptyMsg: "Email is required", ...emailRule } },
    // Phone is optional here, but must be valid when provided. The message is
    // intentionally shorter than the shared one (no digit-count suffix) — a
    // step-specific copy choice for the public contact form.
    phone: {
      value: data.phone,
      rule: { predicate: isValidPhone, invalidMsg: "Enter a valid phone number" },
    },
    message: { value: data.message, rule: { required: true, emptyMsg: "Message is required" } },
  });
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  useSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonical: "/contact",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
    ],
  });

  // Reveal-then-live: the map recomputes every render, so once revealed each
  // field's message clears as it becomes valid — no keystroke reset needed.
  const errors = validateContactForm(formData);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      return;
    }

    setIsSubmitting(true);
    const subject = `Website message from ${formData.name}`;
    const body = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || "Not provided"}`,
      "",
      formData.message,
    ].join("\n");
    setSubmitted(true);
    setIsSubmitting(false);
    window.location.assign(
      `mailto:hello@accent-productions.co.nz?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    );
    window.scrollTo({ top: 0 });
  };

  const errorInputStyles = "border-red-500";

  if (submitted) {
    return (
      <main className="min-h-screen">
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-28 pb-10 md:pb-20">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-gray-900">Say hello<span className="text-orange-600">.</span></h1>
          </div>
        </section>

        <section className="pb-16 md:pb-24 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-3">Message Sent</h2>
            <p className="text-lg text-gray-600 mb-2 font-medium">
              Thanks for getting in touch{formData.name ? `, ${formData.name.split(" ")[0]}` : ""}.
            </p>
            <p className="text-gray-500 mb-8">
              We&apos;ll respond to <span className="font-medium text-gray-700">{formData.email}</span> within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-block bg-orange-600 text-white px-8 py-3 rounded-md font-bold"
              >
                Return Home
              </Link>
              <a
                href={HIREOS_INQUIRY_URL}
                className="inline-block bg-white text-black px-8 py-3 rounded-md font-bold border border-gray-300 cursor-pointer"
              >
                Get a Quote
              </a>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      {/* Header — oversized page title */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-28 pb-10 md:pb-20">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-gray-900 mb-5 md:mb-6">Say hello<span className="text-orange-600">.</span></h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-2xl font-medium">
            Send us a message and we&apos;ll get back to you the same day.
          </p>
        </div>
      </section>

      <section className="pb-16 md:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16">

            {/* Form - takes 3 columns */}
            <div className="lg:col-span-3">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-6">Send a Message</h2>

              {showValidation && (
                <Alert variant="error" className="mb-6">
                  Please fill in all required fields.
                </Alert>
              )}


              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name & Phone row */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      aria-invalid={showValidation && Boolean(errors.name)}
                      className={`${inputStyles} ${showValidation && errors.name ? errorInputStyles : "border-gray-300"}`}
                    />
                    {showValidation && errors.name && (
                      <p role="alert" className="text-xs text-red-600 mt-1 font-medium">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      aria-invalid={showValidation && Boolean(errors.phone)}
                      className={`${inputStyles} ${showValidation && errors.phone ? errorInputStyles : "border-gray-300"}`}
                    />
                    {showValidation && errors.phone && (
                      <p role="alert" className="text-xs text-red-600 mt-1 font-medium">{errors.phone}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    aria-invalid={showValidation && Boolean(errors.email)}
                    className={`${inputStyles} ${showValidation && errors.email ? errorInputStyles : "border-gray-300"}`}
                  />
                  {showValidation && errors.email && (
                    <p role="alert" className="text-xs text-red-600 mt-1 font-medium">{errors.email}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    aria-invalid={showValidation && Boolean(errors.message)}
                    className={`${inputStyles} resize-none ${showValidation && errors.message ? errorInputStyles : "border-gray-300"}`}
                  />
                  {showValidation && errors.message && (
                    <p role="alert" className="text-xs text-red-600 mt-1 font-medium">{errors.message}</p>
                  )}
                </div>

                <div className="pt-2">
                  <Button type="submit" size="lg" loading={isSubmitting} className="w-full sm:w-auto">
                    Send Message
                  </Button>
                </div>
              </form>
            </div>

            {/* Contact Info - takes 2 columns */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-6">Get in Touch</h2>

              <div className="border-t border-gray-200">
                <div className="py-4 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-1">Phone</p>
                  <a href="tel:+64276023869" className="text-lg text-gray-900 cursor-pointer font-bold">
                    +64 4 887 8650
                  </a>
                </div>
                <div className="py-4 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-1">Email</p>
                  <a href="mailto:hello@accent-productions.co.nz" className="text-lg text-gray-900 cursor-pointer font-bold break-words">
                    hello@accent-productions.co.nz
                  </a>
                </div>
                <div className="py-4 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-1">Location</p>
                  <p className="text-lg text-gray-900 font-bold">Wellington, New Zealand</p>
                  <p className="text-gray-500 text-sm mt-1">Servicing the greater Wellington region</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}
